import type { Campaign, Task, TaskStatus } from '../types'

export interface AlterationMetric {
  campaignId: string
  campaign: string
  taskId: string
  task: string
  taskStatus: TaskStatus
  taskChanges: number
  deliverableChanges: number
  internalTaskChanges: number
  internalDeliverableChanges: number
  clientTaskChanges: number
  clientDeliverableChanges: number
  internalChanges: number
  clientChanges: number
  clientRejections: number
  totalChanges: number
  campaignTotalChanges: number
  campaignInternalChanges: number
  campaignClientChanges: number
  campaignClientRejections: number
}

function buildAlterationMetrics(tasks: Task[], campaigns: Campaign[], matchesDate: (occurredOn?: string) => boolean): AlterationMetric[] {
  const rows = tasks.map(task => {
    const campaign = campaigns.find(item => item.id === task.campaignId)
    let rejectedByClient = false
    let internalTaskChanges = 0
    let internalDeliverableChanges = 0
    let clientTaskChanges = 0
    let clientDeliverableChanges = 0
    let clientRejections = 0

    const chronologicalEntries = task.activityEntries
      .map((entry, index) => ({ entry, index }))
      .sort((left, right) => (left.entry.occurredOn ?? '').localeCompare(right.entry.occurredOn ?? '') || left.index - right.index)
    chronologicalEntries.forEach(({ entry }) => {
      if (entry.statusChange?.entity === 'task' && entry.statusChange.to === 'Reprovada') {
        rejectedByClient = true
        if (matchesDate(entry.occurredOn)) clientRejections += 1
        return
      }
      if (entry.statusChange?.to !== 'Em alteração' || !matchesDate(entry.occurredOn)) return
      if (rejectedByClient) {
        if (entry.statusChange.entity === 'task') clientTaskChanges += 1
        else clientDeliverableChanges += 1
      } else if (entry.statusChange.entity === 'task') internalTaskChanges += 1
      else internalDeliverableChanges += 1
    })
    const taskChanges = internalTaskChanges + clientTaskChanges
    const deliverableChanges = internalDeliverableChanges + clientDeliverableChanges
    const internalChanges = internalTaskChanges + internalDeliverableChanges
    const clientChanges = clientTaskChanges + clientDeliverableChanges

    return {
      campaignId: task.campaignId,
      campaign: campaign?.name ?? 'Campanha não encontrada',
      taskId: task.id,
      task: task.title,
      taskStatus: task.status,
      taskChanges,
      deliverableChanges,
      internalTaskChanges,
      internalDeliverableChanges,
      clientTaskChanges,
      clientDeliverableChanges,
      internalChanges,
      clientChanges,
      clientRejections,
      totalChanges: taskChanges + deliverableChanges,
      campaignTotalChanges: 0,
      campaignInternalChanges: 0,
      campaignClientChanges: 0,
      campaignClientRejections: 0,
    }
  })
  const totalsByCampaign = rows.reduce<Record<string, { total: number; internal: number; client: number; rejections: number }>>((totals, item) => {
    const current = totals[item.campaignId] ?? { total: 0, internal: 0, client: 0, rejections: 0 }
    totals[item.campaignId] = {
      total: current.total + item.totalChanges,
      internal: current.internal + item.internalChanges,
      client: current.client + item.clientChanges,
      rejections: current.rejections + item.clientRejections,
    }
    return totals
  }, {})

  return rows.map(item => ({
    ...item,
    campaignTotalChanges: totalsByCampaign[item.campaignId].total,
    campaignInternalChanges: totalsByCampaign[item.campaignId].internal,
    campaignClientChanges: totalsByCampaign[item.campaignId].client,
    campaignClientRejections: totalsByCampaign[item.campaignId].rejections,
  }))
    .sort((a, b) => (b.campaignTotalChanges + b.campaignClientRejections) - (a.campaignTotalChanges + a.campaignClientRejections) || (b.totalChanges + b.clientRejections) - (a.totalChanges + a.clientRejections) || a.campaign.localeCompare(b.campaign) || a.task.localeCompare(b.task))
}

export function alterationMetrics(tasks: Task[], campaigns: Campaign[], period?: string) {
  return buildAlterationMetrics(tasks, campaigns, occurredOn => !period || occurredOn?.startsWith(period) === true)
}

export function alterationMetricsForRange(tasks: Task[], campaigns: Campaign[], start: string, end: string) {
  return buildAlterationMetrics(tasks, campaigns, occurredOn => {
    const day = occurredOn?.slice(0, 10)
    return Boolean(day && day >= start && day <= end)
  })
}
